import { useEffect, useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../common/Button';
import { getStripeCustomerPortal, getStripeConnectOnboardingLink, stripeCheckout } from '../../api/stripe';
import {
  disconnectOtterStore,
  getOtterAuthorizeUrl,
  getOtterConnectionStatus,
  OtterConnectionStatus,
  OtterSelectableStore
} from '../../api/otter';
import ChangePasswordForm from './ChangePasswordForm';
import DeleteItemModal from '../DeleteItemModal';
import UserInfoForm from './UserInfoForm';
import { openModal, useModalContext } from '../../contexts/ModalContext';
import { usePermissionsContext } from '../../contexts/PermissionsContext';
import { useRestaurantContext } from '../../contexts/RestaurantContext';
import { getCreateRestaurantPath } from '../../pages/MainContainer/RestaurantWizard/restaurantWizardNavigation';

const AccountInformation = () => {
  const stripe = useStripe();
  const { dispatch } = useModalContext();
  const [isLoadingOnboardingLink, setIsLoadingOnboardingLink] = useState(false);
  const [onboardingUrl, setOnboardingUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isLoadingOtterAuthorizeUrl, setIsLoadingOtterAuthorizeUrl] = useState(false);
  const [otterSelectableStores, setOtterSelectableStores] = useState<OtterSelectableStore[]>([]);
  const [connectingOtterStoreId, setConnectingOtterStoreId] = useState<string | null>(null);
  const [otterConnectionStatus, setOtterConnectionStatus] = useState<OtterConnectionStatus | null>(null);
  const [isDisconnectOtterModalOpen, setIsDisconnectOtterModalOpen] = useState(false);
  const [isDisconnectingOtter, setIsDisconnectingOtter] = useState(false);
  const [otterDisconnectError, setOtterDisconnectError] = useState(false);

  const handleCopyOnboardingUrl = async () => {
    if (!onboardingUrl) return;
    try {
      await navigator.clipboard.writeText(onboardingUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error(err);
      openModal({
        dispatch,
        message: 'Could not copy link. Try selecting and copying manually.'
      });
    }
  };

  const {
    permissions: { hasPairings, hasImageUpload }
  } = usePermissionsContext();
  const isSuper = hasPairings && hasImageUpload;
  const navigate = useNavigate();
  const location = useLocation();
  const { currentRestaurant } = useRestaurantContext();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const encodedStores = params.get('otterSelectStores');
    const connected = params.get('otterConnected');
    const disconnected = params.get('otterDisconnected');
    const otterError = params.get('otterError');

    if (!encodedStores && !connected && !disconnected && !otterError) return;

    // This page was opened in a popup tab for the Otter consent flow and Otter has redirected
    // back here. Hand the result to the tab that opened us so it can pick up where it left off,
    // then close this one instead of showing anything in it.
    if (window.opener && !window.opener.closed) {
      window.opener.location.href = window.location.href;
      window.close();
      return;
    }

    if (encodedStores) {
      try {
        const base64 = encodedStores.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
        setOtterSelectableStores(JSON.parse(atob(padded)));
      } catch (err) {
        console.error(err);
        openModal({ dispatch, message: 'Could not read the Otter store list. Try connecting again.' });
      }
    }

    if (connected) {
      setOtterSelectableStores([]);
      openModal({ dispatch, title: 'Success!', message: 'Otter store connected successfully.' });
    }

    if (disconnected) {
      openModal({ dispatch, title: 'Success!', message: 'Otter store disconnected successfully.' });
    }

    if (otterError) {
      openModal({ dispatch, title: 'Otter not connected', message: otterError });
    }

    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshOtterConnectionStatus = async () => {
    try {
      const status = await getOtterConnectionStatus();
      setOtterConnectionStatus(status);
    } catch (err) {
      // No restaurant currently selected, or the request otherwise failed — fall back to showing
      // the connect flow rather than blocking the page on this being available.
      console.error(err);
      setOtterConnectionStatus(null);
    }
  };

  useEffect(() => {
    // Wait for RestaurantContext to establish the restaurantid cookie before checking connection
    // status — on a fresh page load (e.g. right after the Otter popup hands off via a full
    // navigation) that cookie isn't set yet, and the request would 401. Re-running whenever the
    // selected restaurant changes also keeps the badge correct after switching restaurants.
    if (!currentRestaurant?.restaurantID) return;
    refreshOtterConnectionStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRestaurant?.restaurantID]);

  const openOtterAuthorizeUrl = (url: string) => {
    const popup = window.open(url, '_blank');
    if (!popup) {
      openModal({ dispatch, message: 'Please allow pop-ups for this site to connect to Otter, then try again.' });
    }
  };

  const handleSelectOtterStore = async (store: OtterSelectableStore) => {
    setConnectingOtterStoreId(store.storeId);
    try {
      const { authorize_url: url } = await getOtterAuthorizeUrl(`${store.brandId}:${store.storeId}`);
      if (url) {
        openOtterAuthorizeUrl(url);
      } else {
        openModal({ dispatch, message: 'Could not generate link. Try again or contact support.' });
      }
    } catch (err) {
      console.error(err);
      openModal({ dispatch, message: 'Could not connect this store. Try again or contact support.' });
    } finally {
      setConnectingOtterStoreId(null);
    }
  };

  const handleDisconnectOtterStore = async () => {
    setIsDisconnectingOtter(true);
    try {
      // Completes in the consent popup; the hand-off reloads this page with `otterDisconnected=1`.
      const { authorize_url: url } = await disconnectOtterStore();
      setIsDisconnectOtterModalOpen(false);
      openOtterAuthorizeUrl(url);
    } catch (err) {
      console.error(err);
      setOtterDisconnectError(true);
    } finally {
      setIsDisconnectingOtter(false);
    }
  };

  const getCustomerPortal = async () => {
    try {
      const url = await getStripeCustomerPortal();
      window.location.replace(url);
    } catch (err) {
      console.error(err);

      // This is the no code customer portal that Stripe provides.  For more information: https://docs.stripe.com/customer-management/activate-no-code-customer-portal
      // We have some customers that were set up manually within Stripe's dashboard instead of our onboarding process built with Stripe Checkout.  Since the stripe customer ID is not in our database in this edge case,
      // it'll fail getting the Stripe customer dashboard for the customer.  The alternative is to take them to Stripe's customer portal for account management when it comes to payments.
      window.location.replace('https://billing.stripe.com/p/login/28odR43Be5y54Uw6oo');
    }
  };

  const getStripeCheckout = async () => {
    if (isSuper) {
      navigate(getCreateRestaurantPath());
      return;
    }

    const id = await stripeCheckout();
    const result = await stripe.redirectToCheckout({ sessionId: id });
    if (result.error) {
      openModal({
        dispatch,
        message:
          'An unexpected error has occurred. Please email our team at info@taptabapp.com so that we can address this issue promptly.'
      });
    }
  };

  const handleGenerateOnboardingUrl = async () => {
    setOnboardingUrl(null);
    setIsLoadingOnboardingLink(true);
    try {
      const { onboarding_url: url } = await getStripeConnectOnboardingLink();
      if (url) {
        setOnboardingUrl(url);
      } else {
        openModal({
          dispatch,
          message: 'Could not generate link. Try again or contact support.'
        });
      }
    } catch (err) {
      console.error(err);
      openModal({
        dispatch,
        message: 'Could not generate link. Try again or contact support.'
      });
    } finally {
      setIsLoadingOnboardingLink(false);
    }
  };

  const handleGenerateOtterAuthorizeUrl = async () => {
    setIsLoadingOtterAuthorizeUrl(true);
    try {
      const { authorize_url: url } = await getOtterAuthorizeUrl();
      if (url) {
        openOtterAuthorizeUrl(url);
      } else {
        openModal({
          dispatch,
          message: 'Could not generate link. Try again or contact support.'
        });
      }
    } catch (err) {
      console.error(err);
      openModal({
        dispatch,
        message: 'Could not generate link. Try again or contact support.'
      });
    } finally {
      setIsLoadingOtterAuthorizeUrl(false);
    }
  };

  return (
    <div className="account-info-container">
      <UserInfoForm />
      <hr />
      <h2 className="account-info-input-header">CHANGE PASSWORD</h2>
      <ChangePasswordForm />
      <hr />
      <div className="account-info-stripe-container">
        <div className="account-info-current-services">
          <h2 className="account-info-input-header">CURRENT SERVICES</h2>
          <Button className="manage-services-button" onClick={getCustomerPortal}>
            MANAGE SERVICES
          </Button>
        </div>
        <div className="account-info-new-service">
          <h2 className="account-info-input-header">GENERATE STRIPE ONBOARDING URL</h2>
          <Button
            className="add-service-button generate-onboarding-url-button"
            onClick={handleGenerateOnboardingUrl}
            isLoading={isLoadingOnboardingLink}
            isDisabled={isLoadingOnboardingLink}
          >
            GENERATE URL
          </Button>
          {onboardingUrl && (
            <div className="account-info-onboarding-url-copy">
              <input
                type="text"
                readOnly
                value={onboardingUrl}
                className="account-info-onboarding-url-input"
                aria-label="Onboarding URL"
              />
              <button
                type="button"
                onClick={handleCopyOnboardingUrl}
                className="account-info-onboarding-url-copy-button"
                title={copied ? 'Copied!' : 'Copy link'}
                aria-label={copied ? 'Copied!' : 'Copy link'}
              >
                {copied ? (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden
                  >
                    <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2" />
                    <path
                      d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>
        <div className="account-info-new-service">
          <h2 className="account-info-input-header">ADD SERVICES</h2>
          <Button className="add-service-button" onClick={getStripeCheckout}>
            ADD RESTAURANT
          </Button>
        </div>
        <div className="account-info-new-service">
          <h2 className="account-info-input-header">OTTER</h2>
          {otterConnectionStatus?.connected ? (
            <div className="otter-connected-status">
              <div className="add-service-button otter-connected-pill">CONNECTED</div>
              {otterConnectionStatus.otterStoreId && (
                <span className="otter-connected-store-id">Store ID: {otterConnectionStatus.otterStoreId}</span>
              )}
              {isSuper && (
                <Button
                  className="add-service-button otter-disconnect-button"
                  onClick={() => setIsDisconnectOtterModalOpen(true)}
                >
                  DISCONNECT
                </Button>
              )}
            </div>
          ) : (
            <Button
              className="add-service-button"
              onClick={handleGenerateOtterAuthorizeUrl}
              isLoading={isLoadingOtterAuthorizeUrl}
              isDisabled={isLoadingOtterAuthorizeUrl}
            >
              CONNECT TO OTTER
            </Button>
          )}
          {otterSelectableStores.length > 0 && (
            <div className="otter-store-picker">
              <p>This Otter organization has multiple stores. Select the one to connect:</p>
              <ul className="otter-store-picker-list">
                {otterSelectableStores.map((store) => (
                  <li key={store.storeId} className="otter-store-picker-item">
                    <span>
                      {store.storeName} <em>({store.brandName})</em>
                    </span>
                    <Button
                      className="add-service-button"
                      onClick={() => handleSelectOtterStore(store)}
                      isLoading={connectingOtterStoreId === store.storeId}
                      isDisabled={connectingOtterStoreId !== null}
                    >
                      CONNECT
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <DeleteItemModal
            title="DISCONNECT OTTER"
            message="Please confirm that you would like to disconnect this restaurant from Otter. Menu syncing will stop until it's reconnected."
            error={
              otterDisconnectError &&
              'An unexpected error has occurred while disconnecting. Unable to perform action at this time.'
            }
            isOpen={isDisconnectOtterModalOpen}
            onCancel={() => {
              setIsDisconnectOtterModalOpen(false);
              setOtterDisconnectError(false);
            }}
            onConfirm={handleDisconnectOtterStore}
            confirmLabel={isDisconnectingOtter ? 'DISCONNECTING...' : 'DISCONNECT'}
          />
        </div>
      </div>
    </div>
  );
};

export default AccountInformation;
